"use client";

import { useChat } from "@ai-sdk/react";
import type { UIMessage } from "ai";
import { DefaultChatTransport } from "ai";
import { Mic, Volume2 } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, startTransition } from "react";
import type { FormEvent } from "react";

import { ChatThinkingWait } from "./ChatThinkingWait";

import { AssistantMessageContent } from "./AssistantMessageContent";
import {
  clearAllAutoLaunchStorage,
  recordWidgetManualPanelClose,
  writeVisitorEngagedHint,
} from "./auto-launch-storage";
import { trackAutolaunchEvent } from "./autolaunch-telemetry";
import { forgetWidgetSession } from "./session-delete-client";
import {
  assistantDisplayText,
  resolveAssistantModelCta,
  type SiteCtaConfig,
} from "@/lib/engage/model-cta";
import {
  formatAssistantBubbleText,
  isAssistantMessageDisplayReady,
  isChatTurnInFlight,
} from "@/lib/engage/chat-ui-message";
import { useThinkingPencilAfterDelay } from "@/lib/engage/use-thinking-pencil-after-delay";
import { toTtsSpeakableText } from "@/lib/engage/tts-speakable-text";
import type { WidgetConfig } from "./widget-config";
import { useAutoLaunch } from "./useAutoLaunch";
import { useSkinnySession } from "./useSkinnySession";
import { useWidgetVoice } from "./useWidgetVoice";
import { useWidgetSiteExtras } from "./use-widget-site-extras";
import { useEngageSuggest } from "./useEngageSuggest";
import { EngageGhostTextInput } from "./EngageGhostTextInput";
import {
  onFirstUserMessage,
  onLaunched,
  onPanelClose,
  type FollowupState,
} from "@/lib/widget/autolaunch-followup";
import {
  resolveBubbleClassName,
  resolveBubbleInlineStyle,
  resolveHeaderBackground,
  resolveHeaderBorderRadius,
} from "@/lib/engage/widget-appearance";
import {
  WIDGET_LEADING_PRIMARY,
  WIDGET_TEXT_CONTROL,
  WIDGET_TEXT_MICRO,
  WIDGET_TEXT_PRIMARY,
  WIDGET_TEXT_SECONDARY,
} from "@/lib/engage/widget-typography";
import { readStakeholderChipDraft } from "@/lib/engage/stakeholder-preview-chip-draft";
import { CHAT_HELP_EVENT, readChatHelpDetail } from "@/lib/content/chat-help";
import {
  engageWidgetChatCredentials,
  formatPortalAskEngageMessage,
  PORTAL_ASK_ENGAGE_EVENT,
  PORTAL_OPERATOR_HELP_CONTROL_FIELD,
  PORTAL_OPERATOR_HELP_FLAG,
  PORTAL_OPERATOR_HELP_SLUG_FIELD,
  readPortalAskEngageDetail,
  type PortalAskEngageDetail,
} from "@/lib/content/portal-ask-engage";
import { WidgetPoweredByBranding } from "./WidgetPoweredByBranding";
import { useIsMobileViewport } from "./use-widget-mobile-layout";

export interface EngageChatInterfaceProps {
  readonly config: WidgetConfig;
  readonly panelOpen: boolean;
  /** Full-viewport mobile sheet (#1154) — enables safe-area input padding. */
  readonly mobileSheetOpen?: boolean;
  readonly onRequestOpenPanel: () => void;
  readonly onClosePanel?: () => void;
}

function chatApiUrl(config: WidgetConfig): string {
  const base = config.apiOrigin.replace(/\/$/, "");
  return `${base}/api/engage/chat`;
}

function extractMessageText(message: UIMessage): string {
  return message.parts
    .filter((part): part is { type: "text"; text: string } => part.type === "text")
    .map((part) => part.text)
    .join("");
}

function assistantTextPartsTerminal(message: UIMessage): boolean {
  const textParts = message.parts.filter((p) => p.type === "text");
  if (textParts.length === 0) {
    return false;
  }
  return textParts.every((p) => {
    if (!("state" in p)) {
      return true;
    }
    const state = (p as { state?: unknown }).state;
    return state === undefined || state === "done";
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Extract plain text from an AI SDK message that may carry `content` (string) or `parts`. */
function extractTextFromSdkMessage(msg: Record<string, unknown>): string {
  if (typeof msg.content === "string") return msg.content;
  if (Array.isArray(msg.parts)) {
    return msg.parts
      .map((p) => {
        const part = p as Record<string, unknown>;
        return typeof part.text === "string" ? part.text : "";
      })
      .join("");
  }
  return "";
}

function buildWidgetChatFetch(
  tenantId: string,
  siteId: string,
  sessionId: string,
  onSessionId?: (sessionId: string) => void,
  testToken?: string,
  portalHelp?: {
    readonly portalSession: boolean;
    readonly takeHelpFocus: () => PortalAskEngageDetail | null;
  },
): typeof globalThis.fetch {
  return async (url, init) => {
    if (init && typeof init.body === "string") {
      try {
        const parsedBody: unknown = JSON.parse(init.body);
        if (!isRecord(parsedBody)) {
          return globalThis.fetch(url, init);
        }

        const msgs = Array.isArray(parsedBody.messages) ? parsedBody.messages : [];
        let currentMessage = "";
        if (msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          if (isRecord(lastMsg)) {
            currentMessage = extractTextFromSdkMessage(lastMsg);
          }
        }

        const history = msgs.slice(0, -1).map((m) => {
          const msg = m as Record<string, unknown>;
          return { role: msg.role, content: extractTextFromSdkMessage(msg) };
        });
        const { messages, ...restOfBody } = parsedBody;
        void messages;

        const s2rPayload: Record<string, unknown> = {
          ...restOfBody,
          message: currentMessage,
          history,
          tenant_id: tenantId,
          site_id: siteId,
          session_id: sessionId,
          ...(testToken ? { test_token: testToken } : {}),
        };
        if (portalHelp?.portalSession) {
          s2rPayload[PORTAL_OPERATOR_HELP_FLAG] = true;
          const focus = portalHelp.takeHelpFocus();
          if (focus) {
            s2rPayload[PORTAL_OPERATOR_HELP_SLUG_FIELD] = focus.slug;
            if (focus.control) {
              s2rPayload[PORTAL_OPERATOR_HELP_CONTROL_FIELD] = focus.control;
            }
          }
        }

        const res = await globalThis.fetch(url, {
          ...init,
          body: JSON.stringify(s2rPayload),
        });

        const headerSessionId = res.headers.get("X-Session-Id") ?? res.headers.get("x-session-id");
        if (headerSessionId?.trim()) {
          onSessionId?.(headerSessionId.trim());
        }

        return res;
      } catch (error) {
        console.error("[EngageChatInterface] Failed to reshape chat payload:", error);
      }
    }
    return globalThis.fetch(url, init);
  };
}

export function EngageChatInterface({
  config,
  panelOpen,
  mobileSheetOpen = false,
  onRequestOpenPanel,
  onClosePanel,
}: EngageChatInterfaceProps) {
  const { sessionId, clearSession, adoptSessionId, isStateless } = useSkinnySession({
    tenantId: config.tenantId,
    siteId: config.siteId,
  });
  const [helpFocus] = useState(() => ({ current: null as PortalAskEngageDetail | null }));
  const portalSession = config.portalSession === true;

  const customFetch = useCallback(
    (url: RequestInfo | URL, init?: RequestInit) =>
      buildWidgetChatFetch(
        config.tenantId,
        config.siteId,
        sessionId,
        adoptSessionId,
        config.testToken,
        {
          portalSession,
          takeHelpFocus: () => {
            const pending = helpFocus.current;
            helpFocus.current = null;
            return pending;
          },
        },
      )(url, init),
    [adoptSessionId, config.siteId, config.tenantId, config.testToken, helpFocus, portalSession, sessionId],
  );

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: chatApiUrl(config),
        credentials: engageWidgetChatCredentials(portalSession),
        fetch: customFetch,
      }),
    [config, customFetch, portalSession],
  );

  const { messages, sendMessage, status, setMessages, stop, error, clearError } = useChat({
    transport,
  });

  const {
    autoRead,
    toggleAutoRead,
    isListening,
    startListening,
    stopListening,
    speakTts,
    stopPlayback,
    speechRecognitionSupported,
  } = useWidgetVoice(config);

  const { cta, quickReplies, chatPersistenceDisabled, intentAssistEnabled } =
    useWidgetSiteExtras(config);

  /** True only when resume hydrated prior messages (auto-launch returning-visitor signal). */
  const [hasResumedHistory, setHasResumedHistory] = useState(false);
  const resumeInFlightRef = useRef(false);
  /** Session ids for which resume already settled (incl. empty history) — stops fetch loops. */
  const resumeSettledSessionRef = useRef<string | null>(null);
  const chatIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (chatIdRef.current === null) {
      chatIdRef.current = Math.random().toString(36).slice(2, 8);
    }
  }, []);

  const resumeFromServer = useCallback(async () => {
    if (isStateless || chatPersistenceDisabled) return;
    if (resumeSettledSessionRef.current === sessionId) return;
    if (resumeInFlightRef.current) return;

    resumeInFlightRef.current = true;
    const requestSessionId = sessionId;
    const apiOrigin = config.apiOrigin.replace(/\/$/, "");
    const tenantId = config.tenantId;
    const siteId = config.siteId;
    const testToken = config.testToken;
    try {
      const url = `${apiOrigin}/api/engage/chat`;
      const payload = {
        resume: true,
        session_id: requestSessionId,
        tenant_id: tenantId,
        site_id: siteId,
        history_limit: 8,
        ...(testToken ? { test_token: testToken } : {}),
      };
      const res = await globalThis.fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: engageWidgetChatCredentials(config.portalSession === true),
        body: JSON.stringify(payload),
      });
      if (!res.ok) return;

      resumeSettledSessionRef.current = requestSessionId;

      const headerSessionId = res.headers.get("X-Session-Id") ?? res.headers.get("x-session-id");
      if (headerSessionId?.trim()) {
        adoptSessionId(headerSessionId.trim());
      }

      const data: unknown = await res.json();
      if (!data || typeof data !== "object") return;
      const d = data as Record<string, unknown>;
      const history = Array.isArray(d.history) ? d.history : [];
      if (history.length === 0) return;
      const uiMessages: UIMessage[] = history
        .filter(
          (h): h is { role: string; content: string; message_id?: string } =>
            h != null &&
            typeof h === "object" &&
            typeof (h as Record<string, unknown>).role === "string" &&
            typeof (h as Record<string, unknown>).content === "string",
        )
        .map((h, idx) => ({
          id: h.message_id ?? `resumed-${idx}`,
          role: h.role as "user" | "assistant",
          parts: [{ type: "text" as const, text: h.content }],
          createdAt: new Date(),
        }));
      if (uiMessages.length > 0) {
        setMessages(uiMessages);
        setHasResumedHistory(true);
      }
    } catch (err) {
      console.warn(
        "[EngageChatInterface] resume_failed",
        err instanceof Error ? err.message : err,
      );
    } finally {
      resumeInFlightRef.current = false;
    }
  }, [
    adoptSessionId,
    chatPersistenceDisabled,
    config.apiOrigin,
    config.portalSession,
    config.siteId,
    config.tenantId,
    config.testToken,
    isStateless,
    sessionId,
    setMessages,
  ]);

  useEffect(() => {
    if (resumeSettledSessionRef.current === sessionId) return;
    queueMicrotask(() => {
      void resumeFromServer();
    });
  }, [resumeFromServer, sessionId]);

  useEffect(() => {
    const handler = (event: PageTransitionEvent) => {
      if (event.persisted) {
        resumeSettledSessionRef.current = null;
        void resumeFromServer();
      }
    };
    window.addEventListener("pageshow", handler);
    return () => window.removeEventListener("pageshow", handler);
  }, [resumeFromServer]);

  const lastSpokenAssistantIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!autoRead) {
      stopPlayback();
    }
  }, [autoRead, stopPlayback]);

  useEffect(() => {
    if (!autoRead || status !== "ready") {
      return;
    }
    const last = messages.at(-1);
    if (!last || last.role !== "assistant") {
      return;
    }
    if (!assistantTextPartsTerminal(last)) {
      return;
    }
    const raw = extractMessageText(last);
    // Extract response.text from structured model JSON (same as bubble display).
    const plain = toTtsSpeakableText(raw);
    if (!plain) {
      return;
    }
    if (lastSpokenAssistantIdRef.current === last.id) {
      return;
    }
    lastSpokenAssistantIdRef.current = last.id;
    void speakTts(plain).catch((err) => {
      console.warn("[EngageChatInterface] auto_read_tts_failed", err instanceof Error ? err.message : err);
    });
  }, [autoRead, messages, speakTts, status]);

  const hasActiveConversation = useMemo(
    () => messages.some((m) => m.role === "user"),
    [messages],
  );

  // #155 Item 3 — follow-up telemetry tracker. Set when an auto-launch
  // fires; cleared on the first of either (a) the visitor closing the
  // panel manually → `dismissed`, or (b) the visitor sending their
  // first message in the cycle → `user_replied`. The pure-fn state
  // machine in `lib/widget/autolaunch-followup` enforces mutual
  // exclusion + no-op semantics for FAB-initiated opens.
  const followupStateRef = useRef<FollowupState>(null);

  useAutoLaunch({
    config,
    isStateless,
    panelOpen,
    hasActiveConversation,
    hasResumedHistory,
    sessionId,
    setMessages,
    setIsOpen: (open) => {
      if (open) {
        onRequestOpenPanel();
      }
    },
    onFired: (configId) => {
      followupStateRef.current = onLaunched(configId);
    },
  });

  const handleClosePanel = useCallback(() => {
    const decision = onPanelClose(followupStateRef.current);
    followupStateRef.current = decision.nextState;
    if (decision.emit && decision.type) {
      trackAutolaunchEvent(config, {
        type: decision.type,
        config_id: decision.configId ?? null,
        session_id: sessionId,
      });
    }
    recordWidgetManualPanelClose(config.tenantId, config.siteId);
    onClosePanel?.();
  }, [config, onClosePanel, sessionId]);

  const isLoading = isChatTurnInFlight(status);
  const showThinkingPencil = useThinkingPencilAfterDelay(isLoading);

  const [input, setInput] = useState("");
  const { suggestions: intentSuggestions } = useEngageSuggest({
    enabled: intentAssistEnabled,
    apiOrigin: config.apiOrigin,
    tenantId: config.tenantId,
    siteId: config.siteId,
    partial: input,
    // page_url boost is Phase 2; omit until client-mounted boost is wired.
  });

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
  const prevPanelOpenRef = useRef(false);
  const isMobileViewport = useIsMobileViewport();

  const scrollMessagesToEnd = useCallback((behavior: ScrollBehavior = "auto") => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  }, []);

  useEffect(() => {
    if (!panelOpen || !isMobileViewport || typeof window === "undefined") {
      return undefined;
    }

    const sync = () => {
      scrollMessagesToEnd("auto");
    };

    const vv = window.visualViewport;
    vv?.addEventListener("resize", sync);
    vv?.addEventListener("scroll", sync);
    return () => {
      vv?.removeEventListener("resize", sync);
      vv?.removeEventListener("scroll", sync);
    };
  }, [isMobileViewport, panelOpen, scrollMessagesToEnd]);

  const focusChatInput = useCallback(() => {
    chatInputRef.current?.focus({ preventScroll: true });
  }, []);

  /** Panel slide-in is 300ms; FAB click leaves focus on the launcher until we blur it. */
  const scheduleFocusAfterPanelOpen = useCallback(() => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== chatInputRef.current) {
      active.blur();
    }

    focusChatInput();
    const raf = requestAnimationFrame(focusChatInput);
    const t0 = window.setTimeout(focusChatInput, 0);
    const t50 = window.setTimeout(focusChatInput, 50);
    const t320 = window.setTimeout(focusChatInput, 320);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t0);
      clearTimeout(t50);
      clearTimeout(t320);
    };
  }, [focusChatInput]);

  useLayoutEffect(() => {
    const justOpened = panelOpen && !prevPanelOpenRef.current;
    prevPanelOpenRef.current = panelOpen;

    if (!panelOpen || isLoading) {
      return;
    }

    if (justOpened) {
      const chipDraft = readStakeholderChipDraft();
      if (chipDraft) {
        startTransition(() => {
          setInput(chipDraft);
        });
      }
      return scheduleFocusAfterPanelOpen();
    }

    focusChatInput();
    const raf = requestAnimationFrame(focusChatInput);
    return () => cancelAnimationFrame(raf);
  }, [focusChatInput, isLoading, panelOpen, scheduleFocusAfterPanelOpen]);

  useEffect(() => {
    scrollMessagesToEnd("smooth");
  }, [messages, scrollMessagesToEnd, status]);

  const handleChatInputFocus = useCallback(() => {
    requestAnimationFrame(() => {
      scrollMessagesToEnd("auto");
    });
  }, [scrollMessagesToEnd]);

  const showAvatars = config.showAvatars !== false;

  const headerStyle = useMemo(
    () => ({
      background: resolveHeaderBackground(config.userBubbleColor, config.headerGradient),
      ...resolveHeaderBorderRadius(config.cornerRadius),
    }),
    [config.cornerRadius, config.headerGradient, config.userBubbleColor],
  );

  const bubbleClassName = useMemo(
    () => resolveBubbleClassName(config.bubbleShape, config.cornerRadius),
    [config.bubbleShape, config.cornerRadius],
  );

  const surfaceStyle = useMemo(
    () => ({
      backgroundColor: config.surfaceColor,
    }),
    [config.surfaceColor],
  );

  const [isForgetting, setIsForgetting] = useState(false);

  const handleForgetMe = useCallback(() => {
    if (isForgetting) return;
    const previousSessionId = sessionId;
    setIsForgetting(true);
    resumeInFlightRef.current = false;
    resumeSettledSessionRef.current = null;
    setHasResumedHistory(false);
    try {
      stop();
    } catch {
      /* ignore */
    }
    clearError();
    setMessages([]);
    lastSpokenAssistantIdRef.current = null;
    stopPlayback();
    // Best-effort server delete first (uses the OLD session id), then
    // local rotation + storage wipe. If the server call fails we still
    // rotate locally so the visitor sees their intent honored.
    void forgetWidgetSession(config, previousSessionId).finally(() => {
      clearSession();
      clearAllAutoLaunchStorage(config.tenantId, config.siteId);
      setIsForgetting(false);
    });
  }, [
    clearError,
    clearSession,
    config,
    isForgetting,
    sessionId,
    setMessages,
    stop,
    stopPlayback,
  ]);

  const submitUserMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) {
        return;
      }
      // #155 Item 3 — fire `user_replied` exactly once per auto-launch
      // cycle, BEFORE the send round-trip starts so attribution lands
      // even if the send itself fails. Reading the rejection guards
      // above ensures we only mark a real, non-empty submission.
      const decision = onFirstUserMessage(followupStateRef.current);
      followupStateRef.current = decision.nextState;
      if (decision.emit && decision.type) {
        trackAutolaunchEvent(config, {
          type: decision.type,
          config_id: decision.configId ?? null,
          session_id: sessionId,
        });
      }
      // #155 Item 4 — record the durable "visitor engaged here" hint.
      // Idempotent + storage-failure-safe; future page loads consult
      // this in `useAutoLaunch` and suppress the greet when the
      // operator has opted in to returning-visitor suppression.
      writeVisitorEngagedHint(config.tenantId, config.siteId);
      clearError();
      void sendMessage({ text: trimmed });
      setInput("");
    },
    [clearError, config, isLoading, sendMessage, sessionId],
  );

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      submitUserMessage(input);
    },
    [input, submitUserMessage],
  );

  useEffect(() => {
    if (!portalSession) return undefined;
    const onAsk = (event: Event) => {
      const detail = readPortalAskEngageDetail(event);
      if (!detail || isLoading) return;
      helpFocus.current = detail;
      onRequestOpenPanel();
      submitUserMessage(formatPortalAskEngageMessage(detail));
    };
    const onChatHelp = (event: Event) => {
      const detail = readChatHelpDetail(event);
      if (!detail || isLoading) return;
      onRequestOpenPanel();
      submitUserMessage(detail.question);
    };
    window.addEventListener(PORTAL_ASK_ENGAGE_EVENT, onAsk);
    window.addEventListener(CHAT_HELP_EVENT, onChatHelp);
    return () => {
      window.removeEventListener(PORTAL_ASK_ENGAGE_EVENT, onAsk);
      window.removeEventListener(CHAT_HELP_EVENT, onChatHelp);
    };
  }, [helpFocus, isLoading, onRequestOpenPanel, portalSession, submitUserMessage]);

  const siteCtaConfig: SiteCtaConfig = {
    ctaEnabled: cta.ctaEnabled,
    ctaLabel: cta.ctaLabel,
    ctaUrl: cta.ctaUrl ?? "",
  };

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      data-s2r-component="engage-chat-interface"
    >
      <div
        className="sticky top-0 z-10 flex shrink-0 items-center justify-between gap-2 border-b border-zinc-200/80 px-3 py-2.5 text-white shadow-sm dark:border-zinc-700/80"
        style={headerStyle}
        data-s2r-component="widget-chat-header"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {showAvatars ? (
            <span
              className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/20 ${WIDGET_TEXT_MICRO} font-bold`}
              aria-hidden
            >
              AI
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <h2 className={`truncate ${WIDGET_TEXT_PRIMARY} font-semibold tracking-tight`}>{config.chatWindowTitle}</h2>
            {chatPersistenceDisabled ? (
              <p
                className={`truncate ${WIDGET_TEXT_MICRO} font-medium uppercase tracking-wide text-white/80`}
                data-s2r-component="widget-chat-not-stored-notice"
              >
                Conversations are not stored on our servers
              </p>
            ) : isStateless ? (
              <p
                className={`truncate ${WIDGET_TEXT_MICRO} font-medium uppercase tracking-wide text-white/80`}
                data-s2r-component="widget-private-mode-notice"
              >
                Private mode — session won&apos;t survive refresh
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            className={`inline-flex h-9 min-w-[44px] items-center justify-center rounded-lg px-2 ${WIDGET_TEXT_CONTROL} font-semibold text-white/95 ring-1 ring-white/30 transition-colors hover:bg-white/10 disabled:opacity-60`}
            type="button"
            onClick={handleForgetMe}
            disabled={isForgetting}
            aria-label="Forget this conversation"
            title="Delete this conversation from our servers and start over"
            data-s2r-component="widget-forget-me"
          >
            {isForgetting ? "Forgetting…" : "Forget me"}
          </button>
          {onClosePanel ? (
            <button
              aria-label="Close chat panel"
              className="inline-flex h-9 min-w-9 items-center justify-center rounded-lg text-white/90 ring-1 ring-white/30 transition-colors hover:bg-white/10"
              type="button"
              onClick={handleClosePanel}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div
          className={`shrink-0 border-b border-red-200 bg-red-50 px-3 py-2 ${WIDGET_TEXT_CONTROL} text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-100`}
          data-s2r-component="widget-chat-error"
        >
          <div className="flex items-start justify-between gap-2">
            <p className="min-w-0 flex-1 leading-snug">{error.message}</p>
            <button
              className="shrink-0 font-semibold text-red-700 underline dark:text-red-200"
              type="button"
              onClick={() => clearError()}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      <div
        className="min-h-0 flex-1 overflow-y-auto px-3 py-3"
        data-s2r-component="widget-message-list"
        style={surfaceStyle}
      >
        {messages.length === 0 ? (
          <div className={`flex h-full min-h-[120px] items-center justify-center text-center ${WIDGET_TEXT_PRIMARY} text-zinc-500 dark:text-zinc-400`}>
            <p>Ask a question to get started.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {messages.map((m, idx) => {
              const isUser = m.role === "user";
              const rawText = extractMessageText(m);
              const displayReady =
                isUser ||
                isAssistantMessageDisplayReady({
                  role: m.role,
                  messageIndex: idx,
                  messageCount: messages.length,
                  chatStatus: status,
                  message: m,
                });
              const text = isUser
                ? rawText
                : formatAssistantBubbleText(rawText, displayReady, assistantDisplayText);
              const showThinking = !isUser && !displayReady;
              const modelCta =
                isUser || !displayReady
                  ? null
                  : resolveAssistantModelCta(rawText, siteCtaConfig);
              const bubbleStyle = resolveBubbleInlineStyle(
                isUser,
                config.bubbleShape,
                isUser
                  ? {
                      backgroundColor: config.userBubbleColor,
                      color: config.userTextColor,
                    }
                  : {
                      backgroundColor: config.replyBubbleColor,
                      color: config.replyTextColor,
                    },
              );
              const bubble = (
                <div
                  className={`max-w-[88%] px-3 py-2 ${WIDGET_TEXT_PRIMARY} ${WIDGET_LEADING_PRIMARY} shadow-sm ${bubbleClassName}`}
                  style={bubbleStyle}
                >
                  {isUser ? (
                    <p className="whitespace-pre-wrap break-words">{text}</p>
                  ) : showThinking ? (
                    <ChatThinkingWait showPencil={showThinkingPencil} />
                  ) : (
                    <>
                      <AssistantMessageContent text={text} />
                      {modelCta ? (
                        <a
                          href={modelCta.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`mt-2 inline-flex w-fit rounded-full border border-current px-4 py-2 ${WIDGET_TEXT_SECONDARY} font-medium no-underline hover:opacity-90`}
                          data-s2r-component="widget-message-cta"
                          onClick={() => {
                            trackAutolaunchEvent(config, {
                              type: "cta_clicked",
                              session_id: sessionId,
                              message_id: modelCta.label,
                            });
                          }}
                        >
                          {modelCta.label}
                        </a>
                      ) : null}
                    </>
                  )}
                </div>
              );
              return (
                <div
                  key={m.id || `msg-${idx}`}
                  className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}
                  data-s2r-component={isUser ? "widget-message-user" : "widget-message-assistant"}
                >
                  {isUser ? (
                    bubble
                  ) : (
                    <div className="flex max-w-full items-start gap-2">
                      {showAvatars ? (
                        <span
                          className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-[9px] font-bold text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300"
                          aria-hidden
                        >
                          AI
                        </span>
                      ) : null}
                      {bubble}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {isLoading && messages.length > 0 && messages[messages.length - 1]?.role === "user" ? (
          <div className="mt-2 flex w-full justify-start" data-s2r-component="widget-chat-typing">
            <div
              className={`flex max-w-[88%] items-center gap-1.5 px-3 py-2 ${WIDGET_TEXT_PRIMARY} ${WIDGET_LEADING_PRIMARY} ${bubbleClassName}`}
              style={resolveBubbleInlineStyle(false, config.bubbleShape, {
                backgroundColor: config.replyBubbleColor,
                color: config.replyTextColor,
              })}
            >
              <ChatThinkingWait showPencil={showThinkingPencil} />
            </div>
          </div>
        ) : null}

        <div ref={messagesEndRef} className="h-px w-full shrink-0" aria-hidden />
      </div>

      <div
        className={`sticky bottom-0 shrink-0 border-t border-zinc-200 bg-white/95 px-3 py-2.5 backdrop-blur-sm dark:border-zinc-800 dark:bg-zinc-950/95 ${
          mobileSheetOpen ? "pb-[max(0.625rem,env(safe-area-inset-bottom))]" : ""
        }`}
        data-s2r-component="widget-chat-input-shell"
        style={surfaceStyle}
      >
        <div
          className="mb-2 flex items-center justify-start"
          data-s2r-component="widget-voice-footer-bar"
        >
          <button
            aria-label={autoRead ? "Disable auto read replies" : "Enable auto read replies"}
            aria-pressed={autoRead}
            className={`inline-flex h-9 min-h-[44px] items-center gap-1.5 rounded-lg px-2.5 ${WIDGET_TEXT_CONTROL} font-semibold tracking-tight ring-1 transition-colors ${
              autoRead
                ? "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-800"
                : "text-zinc-600 ring-zinc-200 hover:bg-zinc-50 dark:text-zinc-400 dark:ring-zinc-700 dark:hover:bg-zinc-900/60"
            }`}
            data-s2r-component="widget-autotts-button"
            type="button"
            onClick={toggleAutoRead}
          >
            <Volume2 className="h-4 w-4 shrink-0" aria-hidden />
            Auto Read
          </button>
        </div>
        {quickReplies.enabled && quickReplies.items.length > 0 && !hasActiveConversation ? (
          <div
            className="mb-2 flex flex-wrap gap-2"
            data-s2r-component="widget-quick-replies"
          >
            {quickReplies.items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`rounded-full border border-zinc-300 bg-white px-3 py-1.5 ${WIDGET_TEXT_SECONDARY} font-medium text-zinc-800 shadow-sm hover:border-emerald-500 hover:text-emerald-700 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100`}
                onClick={() => submitUserMessage(item.message_text)}
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}

        <form
          className="flex items-end gap-2"
          data-s2r-component="widget-input-form"
          onSubmit={onSubmit}
        >
          <label className="sr-only" htmlFor="s2r-widget-chat-input">
            Message
          </label>
          <EngageGhostTextInput
            textareaRef={chatInputRef}
            id="s2r-widget-chat-input"
            autoComplete="off"
            enabled={intentAssistEnabled}
            suggestions={intentSuggestions}
            shellClassName="max-h-28 min-h-[44px] flex-1 rounded-xl border border-zinc-300 bg-white shadow-inner transition-colors focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900"
            className={`max-h-28 min-h-[44px] w-full resize-y border-0 bg-transparent px-3 py-2 ${WIDGET_TEXT_PRIMARY} text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60 dark:text-zinc-100 dark:placeholder:text-zinc-400`}
            disabled={isLoading}
            placeholder="Type your message…"
            rows={2}
            value={input}
            onChange={setInput}
            onFocus={handleChatInputFocus}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <button
            aria-label={isListening ? "Stop voice input" : "Voice input"}
            className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 transition-colors ${
              isListening
                ? "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:ring-amber-800"
                : "text-zinc-600 ring-zinc-200 hover:bg-zinc-50 dark:text-zinc-300 dark:ring-zinc-700 dark:hover:bg-zinc-900/60"
            } disabled:cursor-not-allowed disabled:opacity-40`}
            data-s2r-component="widget-mic-button"
            disabled={isLoading || !speechRecognitionSupported}
            title={
              !speechRecognitionSupported ? "Voice input is not supported in this browser" : undefined
            }
            type="button"
            onClick={() => {
              if (isListening) {
                stopListening();
                return;
              }
              startListening((text) => {
                setInput((prev) => {
                  const next = prev.trim().length > 0 ? `${prev.trim()} ${text}` : text;
                  return next.trim();
                });
              });
            }}
          >
            <Mic className="h-4 w-4" aria-hidden />
          </button>
          <button
            className={`inline-flex h-11 min-w-[44px] shrink-0 items-center justify-center rounded-xl bg-emerald-600 px-4 ${WIDGET_TEXT_PRIMARY} font-semibold text-white shadow-sm shadow-emerald-900/20 transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-emerald-900/40`}
            disabled={isLoading || !input.trim()}
            type="submit"
          >
            Send
          </button>
        </form>
        <WidgetPoweredByBranding />
      </div>
    </div>
  );
}
